'use client'
import { useState, useEffect, useMemo } from 'react'
import Image from 'next/image'
import { Dialog, DialogContent } from '../ui/dialog'
import { X, Phone, Mail } from 'lucide-react'
import { Media, Property, Video } from '@/payload-types'
import MuxPlayer from '@mux/mux-player-react'
import { ImageMedia } from '../Media/ImageMedia'

type Args = {
  property: Property
  dictionary: any
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  lang: string
}

type MediaItem =
  | { type: 'image'; data: { image: Media } }
  | { type: 'video'; data: { video: Video } }

const PropertyImageGallery = ({ property, dictionary, isOpen, setIsOpen, lang }: Args) => {
  const [carouselOpen, setCarouselOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const images = property?.mediaSection?.images || []
  const videos = property?.mediaSection?.videos || []

  
  // Combine images and videos into one list
  const mediaItems = useMemo(() => {
    return [
      ...images.map((img) => ({ type: 'image' as const, data: img })),
      ...videos.map((vid) => ({ type: 'video' as const, data: vid })),
    ]
  }, [images, videos])
  
  const isRTL = lang === 'ar'
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!carouselOpen) return

      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        case 'Escape':
          setCarouselOpen(false)
          break
      }
    }

    if (carouselOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [carouselOpen, currentImageIndex])

  const openCarousel = (index: number) => {
    setCurrentImageIndex(index)
    setCarouselOpen(true)
    setIsOpen(false) // close the dialog before opening the carousel
  }

  const goToNext = () => {
    setCurrentImageIndex((prev) =>
      isRTL
        ? (prev - 1 + mediaItems.length) % mediaItems.length // reversed in RTL
        : (prev + 1) % mediaItems.length
    )
  }

  const goToPrevious = () => {
     setCurrentImageIndex((prev) =>
      isRTL
        ? (prev + 1) % mediaItems.length // reversed in RTL
        : (prev - 1 + mediaItems.length) % mediaItems.length
    )
  }

  const goToImage = (index: number) => {
    setCurrentImageIndex(index)
  }

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const currentItem = mediaItems[currentImageIndex]

    if (currentItem?.type === 'image' && currentItem.data.image) {
      setLoading(true)
    } else if (currentItem?.type === 'video' && currentItem.data.video) {
      setLoading(false)
    }
  }, [currentImageIndex])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-card max-w-screen-2xl p-0 overflow-hidden [&>button]:hidden">
          <div className="flex flex-col">
            <div className="relative w-full h-[65vh]">
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-50"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              {/* Improved responsive grid */}
              <div className="!px-6 !pt-14 !pb-6 w-full h-full overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 auto-rows-fr">
                  {mediaItems.map((item, index) => (
                    <div
                      key={index}
                      className={`
                        relative overflow-hidden rounded-xl group cursor-pointer
                        ${index === 0 && mediaItems.length > 1 ? 'sm:col-span-2 sm:row-span-2' : ''}
                      `}
                      onClick={() => openCarousel(index)}
                    >
                      <div className="aspect-[4/3] w-full">
                       {item.type === 'image' ? (
                          <ImageMedia
                            resource={item?.data?.image}
                            imgClassName="object-cover transition-transform duration-300 group-hover:scale-105"
                            fill
                            alt={
                              (item?.data?.image?.alt as string) ||
                              (item?.data?.image?.filename as string) ||
                              `Property image ${index + 1}`
                            }
                            priority={index < 3}
                            loading="eager"
                          />
                       ) : (
                          <MuxPlayer
                            playbackId={item.data?.video?.mux_playback_id as string}
                            accentColor="#006633"
                            className="object-cover w-full h-full"
                            muted
                            playsInline
                            thumbnailTime={1}
                          />
                       )}
                      </div>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-xl flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                            {item.type === 'image' ? (
                              <svg
                                className="w-6 h-6 text-gray-800"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-6 h-6 text-gray-800"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No images fallback */}
                {mediaItems.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500">
                      <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <p>No images available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-card border-t border-t-border flex items-center justify-between p-4 max-lg:flex-col max-lg:gap-4">
              <div className="flex flex-row items-center gap-6 max-sm:mb-8 max-lg:mb-0">
                {/* Agent Photo */}
                <div className="w-[72px] h-[72px] rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={property?.agency?.Media?.url}
                    alt={property?.agency?.agencyDescriptionSection?.title}
                    className="w-full h-full object-contain rounded-full border border-gray-200"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/100?text=Agent'
                    }}
                  />
                </div>

                {/* Agent Info */}
                <div>
                  <h3 className="text-typography text-h5 font-bold mb-2">
                    {property?.agency?.agencyDescriptionSection?.title}
                  </h3>
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="flex items-center gap-2 max-sm:flex-col max-sm:w-full">
                {property?.agency?.phone && (
                  <a
                    href={`tel:${property?.agency?.phone}`}
                    className="text-typography hover:text-black flex items-center justify-center gap-3 border border-gray-500 rounded-lg px-2 py-3 hover:bg-gray-50 transition-colors w-[140px] max-sm:w-full"
                  >
                    <Phone size={20} />
                    <span className="text-h5 font-semibold">
                      {dictionary?.propertyDetail?.call || 'Call'}
                    </span>
                  </a>
                )}

                {property?.agency?.email && (
                  <a
                    href={`mailto:${property?.agency?.email}`}
                    className="text-typography hover:text-black flex items-center justify-center gap-3 border border-gray-500 rounded-lg px-2 py-3 hover:bg-gray-50 transition-colors w-[140px] max-sm:w-full"
                  >
                    <Mail size={20} />
                    <span className="text-h5 font-semibold">
                      {dictionary?.propertyDetail?.email || 'Email'}
                    </span>
                  </a>
                )}
                
                {property?.agency?.contactSection?.whatsapp && (
                  <a
                    href={`https://wa.me/${property?.agency?.contactSection?.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-typography hover:text-black flex items-center justify-center gap-3 border border-gray-500 rounded-lg px-2 py-3 hover:bg-gray-50 transition-colors w-[140px] max-sm:w-full"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#25D366"
                    >
                      <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.029 18.88c-1.161 0-2.305-.292-3.318-.844l-3.677.964.984-3.595c-.607-1.052-.927-2.246-.926-3.468.001-3.825 3.113-6.937 6.937-6.937 1.856.001 3.598.723 4.907 2.034 1.31 1.311 2.031 3.054 2.03 4.908-.001 3.825-3.113 6.938-6.937 6.938z" />
                    </svg>
                    <span className="text-h5 font-semibold">
                      {dictionary?.propertyDetail?.whatsapp || 'WhatsApp'}
                    </span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Carousel Modal */}
      {carouselOpen && mediaItems.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={() => setCarouselOpen(false)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentImageIndex + 1} / {mediaItems.length}
          </div>

          {/* Main Image */}
          <div className="relative w-full h-full flex items-center justify-center px-16 py-16">
            <div className="relative max-w-7xl max-h-full w-full h-full">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
                  <svg
                    className="animate-spin h-10 w-10 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                </div>
              )}

             {mediaItems && mediaItems?.[currentImageIndex].type === 'image' ? (
                <Image
                  key={mediaItems[currentImageIndex]?.data?.image?.url}
                  src={mediaItems[currentImageIndex]?.data?.image?.url}
                  alt={
                    mediaItems[currentImageIndex]?.data?.image?.alt ||
                    `Property image ${currentImageIndex + 1}`
                  }
                  fill
                  className="object-contain"
                  quality={90}
                  onLoadingComplete={() => setLoading(false)}
                  priority={currentImageIndex === 0 || currentImageIndex === 1}
                />
              ) : (
                <MuxPlayer
                  playbackId={mediaItems[currentImageIndex]?.data?.video?.mux_playback_id as string}
                  accentColor="#006633"
                  className="object-contain w-full h-full"
                  autoPlay
                />
              )}
            </div>
          </div>

          {/* Navigation Arrows */}
          {mediaItems.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Thumbnail Strip */}
          {mediaItems.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4">
              <div className="flex gap-2">
                {mediaItems.map((item: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`
                      relative w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all
                      ${
                        index === currentImageIndex
                          ? 'border-white ring-2 ring-white/50'
                          : 'border-white/30 hover:border-white/60'
                      }
                    `}
                  >
                     {item.type === 'image' ? (
                        <ImageMedia
                          resource={item?.data?.image}
                          imgClassName="object-cover"
                          size="64px"
                          alt={`Thumbnail ${index + 1}`}
                          priority={currentImageIndex === index}
                        />
                      ) : (
                        <div className="relative w-full h-full bg-black flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 text-white opacity-80"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                      
                      {index !== currentImageIndex && (
                        <div className="absolute inset-0 bg-black/40" />
                      )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Click outside to close */}
          <div className="absolute inset-0 -z-10" onClick={() => setCarouselOpen(false)} />
        </div>
      )}
    </>
  )
}

export default PropertyImageGallery
